import React, { useState, useEffect, useContext, useRef } from 'react'
import { toast } from 'react-toastify';
import SidebarContext from '../../sidebarcontext/SidebarContext';
import { Fetchdata } from '../../hooks/getData';
import DataTable from 'react-data-table-component';
import * as XLSX from 'xlsx';
import Tippy from '@tippyjs/react';

const DeviceCode = () => {
    const { appURL, customStyles } = useContext(SidebarContext);
    const userinfo = JSON.parse(localStorage.getItem("userInfo"));

    const [selectedClass, setSelectedClass] = useState("");
    const [selectedSection, setSelectedSection] = useState("");
    const [newDeviceCodeList, setNewDeviceCodeList] = useState([]);
    const [deviceCodeList, setDeviceCodeList] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [classList, setClassList] = useState([]);
    const [sectionList, setSectionList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isPending, setIsPending] = useState(false);
    const [importLoading, setImportLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const searchInput = useRef(null);

    // API to get class list
    useEffect(() => {
        getClassList();
    }, []);

    const getClassList = () => {
        const params = {
            SchID: userinfo?.SchID,
            UserID: userinfo?.UserType === "T" ? userinfo?.UserID : "-1",
            DFlag: "C",
            Flag: "S",
            Type: "POST",
            Academicyear: userinfo?.AcademicYear,
            FetchURL: `${appURL}api/admin/ref-value`,
        };

        Fetchdata(params).then(function (result) {
            if (result.StatusCode === 200) {
                const postResult = result.referencelst ? result.referencelst : "";
                setClassList(postResult);
            } else {
                setClassList([]);
            }
        }).catch(error => {
            console.error("Error fetching class list:", error);
            setClassList([]);
            toast.error("Failed to fetch class list");
        });
    };

    // API to get section list
    useEffect(() => {
        if (selectedClass) {
            getSectionList();
        } else {
            setSectionList([]);
        }
    }, [selectedClass]);

    const getSectionList = () => {
        const params = {
            SchID: userinfo?.SchID,
            UserID: userinfo?.UserType === "T" ? userinfo?.UserID : "-1",
            ClassID: selectedClass,
            DFlag: "S",
            Flag: "S",
            Type: "POST",
            Academicyear: userinfo?.AcademicYear,
            FetchURL: `${appURL}api/admin/ref-value`,
        };

        Fetchdata(params).then(function (result) {
            if (result.StatusCode === 200) {
                const postResult = result.referencelst ? result.referencelst : "";
                setSectionList(postResult);
            } else {
                setSectionList([]);
            }
        }).catch(error => {
            console.error("Error fetching section list:", error);
            setSectionList([]);
            toast.error("Failed to fetch section list");
        });
    };

    // API to get device code list
    const getDeviceCodeList = () => {
        setIsLoading(true);
        const params = {
            SchID: userinfo?.SchID,
            UserID: userinfo?.UserID,
            ClassID: selectedClass || "",
            SectionID: selectedSection || "",
            Flag: "S",
            Type: "POST",
            FetchURL: `${appURL}api/admin/student-bulk-attendance`,
        };

        Fetchdata(params).then(function (result) {
            setIsLoading(false);
            if (result.StatusCode === 200) {
                const postResult = result.AttenLst ? result.AttenLst : [];
                setDeviceCodeList(postResult);
                setNewDeviceCodeList(JSON.parse(JSON.stringify(postResult))); // Deep copy
                setFilteredData(postResult);
            } else {
                setDeviceCodeList([]);
                setNewDeviceCodeList([]);
                setFilteredData([]);
                toast.error(result.Message || "Failed to fetch device codes");
            }
        }).catch(error => {
            setIsLoading(false);
            console.error("Error fetching device codes:", error);
            toast.error("Failed to fetch device codes");
            setDeviceCodeList([]);
            setNewDeviceCodeList([]);
            setFilteredData([]);
        });
    };

    // Fetch device codes when class and section are selected
    useEffect(() => {
        if (selectedClass && selectedSection) {
            getDeviceCodeList();
        } else {
            setDeviceCodeList([]);
            setNewDeviceCodeList([]);
            setFilteredData([]);
        }
    }, [selectedClass, selectedSection]);

    // Search handler
    const searchHandler = (e) => {
        const searchTerm = e.target.value.toLowerCase();
        setSearchText(searchTerm);

        if (searchTerm === "") {
            setFilteredData(deviceCodeList);
        } else {
            const filtered = deviceCodeList.filter(item =>
                item.FullName?.toLowerCase().includes(searchTerm) ||
                item.UserName?.toLowerCase().includes(searchTerm) ||
                item.RegNo?.toLowerCase().includes(searchTerm) ||
                item.DeviceCode?.toLowerCase().includes(searchTerm) ||
                item.DeviceID?.toLowerCase().includes(searchTerm)
            );
            setFilteredData(filtered);
        }
    };

    // Validate DeviceCode is filled
    const validateDeviceCode = () => {
        const invalidItems = newDeviceCodeList.filter(item => {
            return !item.DeviceCode || item.DeviceCode.trim() === "";
        });

        if (invalidItems.length > 0) {
            toast.error(`Please fill Device Code for all students. ${invalidItems.length} student(s) have missing Device Code.`);
            return false;
        }
        return true;
    };

    // Check if a specific row has validation error for DeviceCode
    const hasValidationError = (row) => {
        const currentItem = newDeviceCodeList.find(item => item.UserName === row.UserName);
        return currentItem && (!currentItem.DeviceCode || currentItem.DeviceCode.trim() === "");
    };

    // Download sample Excel template
    const downloadSampleTemplate = () => {
        if (!deviceCodeList.length) {
            toast.warning("Please select class and section first to generate sample template");
            return;
        }

        // Create sample template with existing data
        const templateData = deviceCodeList.map((item) => ({
            "User Name": item.UserName || "",
            "Full Name": item.FullName || "",
            "Reg. No.": item.RegNo || "",
            "Device Code": item.DeviceCode || "",
            "Device ID": item.DeviceID || ""
        }));

        const ws = XLSX.utils.json_to_sheet(templateData);

        // Set column widths
        ws['!cols'] = [
            { wch: 20 },  // User Name
            { wch: 25 },  // Full Name
            { wch: 12 },  // Reg. No.
            { wch: 20 },  // Device Code
            { wch: 15 }   // Device ID
        ];

        // Add instruction sheet
        const instructionData = [
            { Instructions: "HOW TO USE THIS TEMPLATE:" },
            { Instructions: "⚠️ IMPORTANT: Device Code is MANDATORY" },
            { Instructions: "" },
            { Instructions: "1. Do NOT modify the 'User Name', 'Full Name', and 'Reg. No.' columns" },
            { Instructions: "2. 'Device Code' must be filled for every student" },
            { Instructions: "3. 'Device ID' is optional and can be left empty" },
            { Instructions: "4. Each device code should be a unique identifier" },
            { Instructions: "5. Save the file as Excel (.xlsx) format" },
            { Instructions: "6. Click on 'Import Excel' button to upload the updated file" },
            { Instructions: "" },
            { Instructions: "Note: The system will reject any file with missing Device Code" }
        ];

        const wsInstruction = XLSX.utils.json_to_sheet(instructionData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "DeviceCodes_Template");
        XLSX.utils.book_append_sheet(wb, wsInstruction, "Instructions");

        XLSX.writeFile(wb, `DeviceCode_Template_${new Date().toLocaleString()}.xlsx`);
        toast.success("Sample template downloaded successfully");
    };

    // Update device code
    const updateDeviceCode = () => {
        // Validate DeviceCode before updating
        if (!validateDeviceCode()) {
            setIsPending(false);
            return;
        }

        setIsPending(true);
        const params = {
            SchID: userinfo?.SchID,
            UserID: userinfo?.UserID,
            Param: newDeviceCodeList?.map(item => ({
                UserName: item.UserName,
                DeviceCode: item.DeviceCode || "",
                DeviceID: item.DeviceID || ""
            })),
            Flag: "I",
            Type: "POST",
            FetchURL: `${appURL}api/admin/student-bulk-attendance`,
        };

        Fetchdata(params).then(function (result) {
            setIsPending(false);
            if (result.StatusCode === 200) {
                toast.success(result.Message || "Device code updated successfully");
                getDeviceCodeList(); // Refresh the list
            } else {
                toast.error(result.Message || "Failed to update device code");
            }
        }).catch(error => {
            setIsPending(false);
            console.error("Error updating device codes:", error);
            toast.error(error?.Message || error?.message || "Failed to update device code");
        });
    };

    const importDeviceCode = () => {
        // Validate DeviceCode before bulk import
        if (!validateDeviceCode()) {
            setImportLoading(false);
            return;
        }

        setImportLoading(true);
        const params = {
            SchID: userinfo?.SchID,
            UserID: userinfo?.UserID,
            Param: newDeviceCodeList?.map(item => ({
                UserName: item.UserName,
                DeviceCode: item.DeviceCode || "",
                DeviceID: item.DeviceID || ""
            })),
            Flag: "BI",
            Type: "POST",
            FetchURL: `${appURL}api/admin/student-bulk-attendance`,
        };

        Fetchdata(params).then(function (result) {
            setImportLoading(false);
            if (result.StatusCode === 200) {
                toast.success(result.Message || "Device code imported successfully");
                getDeviceCodeList(); // Refresh the list
            } else {
                toast.error(result.Message || "Failed to import device code");
            }
        }).catch(error => {
            setImportLoading(false);
            console.error("Error importing device codes:", error);
            toast.error(error?.Message || error?.message || "Failed to import device code");
        });
    };

    const columns = [
        {
            name: "S.N.",
            selector: (row, index) => index + 1,
            sortable: true,
            width: "80px",
            center: true
        },
        {
            name: 'Reg. No.',
            selector: row => row.RegNo,
            sortable: true,
            grow: 1,
            center: true
        },
        {
            name: "Full Name",
            selector: row => row.FullName,
            sortable: true,
            grow: 2
        },
        {
            name: "User Name",
            selector: row => row.UserName,
            sortable: true,
            grow: 2
        },
        {
            name: "Device Code",
            selector: row => row.DeviceCode,
            sortable: true,
            // grow: 1,
            width: "200px",
            cell: (row, index) => {
                const currentItem = newDeviceCodeList.find(item => item.UserName === row.UserName);
                const hasError = hasValidationError(row);

                return (
                    <div>
                        <input
                            type="text"
                            className={`form-control form-control-sm ${hasError ? 'is-invalid' : ''}`}
                            value={currentItem?.DeviceCode || ''}
                            onChange={(e) => handleInputChange(row, 'DeviceCode', e.target.value)}
                            disabled={isPending}
                            style={{
                                width: '100%',
                                minWidth: '150px',
                                backgroundColor: isPending ? '#f5f5f5' : 'white'
                            }}
                            placeholder="Enter device code"
                        />
                        {hasError && (
                            <div className="invalid-feedback" style={{ fontSize: '11px' }}>
                                Device Code is required
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            name: "Device ID",
            selector: row => row.DeviceID,
            sortable: true,
            // grow: 1,
            cell: (row, index) => {
                const currentItem = newDeviceCodeList.find(item => item.UserName === row.UserName);

                return (
                    <div>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            value={currentItem?.DeviceID || ''}
                            onChange={(e) => handleInputChange(row, 'DeviceID', e.target.value)}
                            disabled={isPending}
                            style={{
                                width: '100%',
                                minWidth: '120px',
                                backgroundColor: isPending ? '#f5f5f5' : 'white'
                            }}
                        // placeholder="Enter device ID (Optional)"
                        />
                    </div>
                );
            }
        },
    ];

    const handleInputChange = (row, field, value) => {
        setNewDeviceCodeList(prevList => {
            const updatedList = prevList.map((item) => {
                if (item.UserName === row.UserName) {
                    return { ...item, [field]: value };
                }
                return item;
            });
            return updatedList;
        });
    };

    const handleUpdate = () => {
        // Validate if there are actual changes
        if (newDeviceCodeList.length === 0) {
            toast.warning("No data to update");
            return;
        }

        // Check for empty DeviceCode
        const hasEmptyDeviceCode = newDeviceCodeList.some(item => {
            return !item.DeviceCode || item.DeviceCode.trim() === "";
        });

        if (hasEmptyDeviceCode) {
            toast.error("Please fill Device Code for all students before updating");
            return;
        }

        const hasChanges = JSON.stringify(deviceCodeList) !== JSON.stringify(newDeviceCodeList);

        if (!hasChanges) {
            toast.info("No changes to update");
            return;
        }

        updateDeviceCode();
    };

    // Handle file import
    const handleFileImport = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Check file extension
        const fileExtension = file.name.split('.').pop().toLowerCase();
        if (fileExtension !== 'xlsx' && fileExtension !== 'xls') {
            toast.error("Please upload a valid Excel file (.xlsx or .xls)");
            return;
        }

        setImportLoading(true);

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                if (!jsonData.length) {
                    toast.error("Excel file is empty");
                    setImportLoading(false);
                    return;
                }

                // Map Excel data to update device codes and device IDs
                const updatedList = newDeviceCodeList.map(item => {
                    const excelRow = jsonData.find(row =>
                        row["User Name"] === item.UserName ||
                        row["UserName"] === item.UserName ||
                        row["Username"] === item.UserName
                    );

                    if (excelRow) {
                        const deviceCode = excelRow["Device Code"] || excelRow["DeviceCode"] || excelRow["Device code"] || "";
                        const deviceID = excelRow["Device ID"] || excelRow["DeviceId"] || excelRow["DeviceID"] || "";

                        return {
                            ...item,
                            DeviceCode: deviceCode.toString(),
                            DeviceID: deviceID.toString()
                        };
                    }
                    return item;
                });

                // Check for missing DeviceCode after import
                const missingDeviceCode = updatedList.filter(item => {
                    return !item.DeviceCode || item.DeviceCode.trim() === "";
                });

                if (missingDeviceCode.length > 0) {
                    toast.error(`Import failed: ${missingDeviceCode.length} student(s) have missing Device Code. Device Code is required for all students.`);
                    setImportLoading(false);
                    return;
                }

                // Check if any changes were made
                const hasChanges = JSON.stringify(deviceCodeList) !== JSON.stringify(updatedList);

                if (!hasChanges) {
                    toast.info("No changes detected in the imported file");
                    setImportLoading(false);
                    return;
                }

                setNewDeviceCodeList(updatedList);
                toast.success(`Successfully imported ${jsonData.length} records. Click Update to save changes.`);

                // Clear file input
                event.target.value = '';

            } catch (error) {
                console.error("Error parsing Excel file:", error);
                toast.error(error.message || "Failed to parse Excel file. Please check the format.");
            } finally {
                setImportLoading(false);
            }
        };

        reader.onerror = () => {
            toast.error("Failed to read the file");
            setImportLoading(false);
        };

        reader.readAsArrayBuffer(file);
    };

    const handleImport = () => {
        if (newDeviceCodeList.length === 0) {
            toast.warning("No data to import");
            return;
        }

        // Validate DeviceCode before bulk import
        const hasEmptyDeviceCode = newDeviceCodeList.some(item => {
            return !item.DeviceCode || item.DeviceCode.trim() === "";
        });

        if (hasEmptyDeviceCode) {
            toast.error("Please fill Device Code for all students before bulk import");
            return;
        }

        importDeviceCode();
    }

    // Reset selections when class/section changes
    const handleClassChange = (value) => {
        setSelectedClass(value);
        setSelectedSection(""); // Reset section when class changes
        setDeviceCodeList([]);
        setNewDeviceCodeList([]);
        setFilteredData([]);
        setSearchText("");
        if (searchInput.current) {
            searchInput.current.value = "";
        }
    };

    // Custom loader component
    const CustomLoader = () => (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '300px', flexDirection: 'column' }}>
            <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading device codes...</p>
        </div>
    );

    // Empty state component
    const EmptyState = () => (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '300px', flexDirection: 'column' }}>
            <i className="fa fa-qrcode" style={{ fontSize: '48px', color: '#ccc' }}></i>
            <p className="mt-2 text-muted">
                {!selectedClass || !selectedSection
                    ? "Please select class and section to view device codes"
                    : searchText
                        ? "No matching records found"
                        : "No device codes found for the selected criteria"}
            </p>
        </div>
    );

    return (
        <div>
            {/* Filter Section */}
            <div className="sec-dataTable">

                {/* Data Table */}
                <DataTable
                    columns={columns}
                    data={searchText ? filteredData : deviceCodeList}
                    customStyles={customStyles}
                    progressPending={isLoading}
                    progressComponent={<CustomLoader />}
                    noDataComponent={<EmptyState />}
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 30, 50, 100]}
                    fixedHeader
                    fixedHeaderScrollHeight="500px"
                    highlightOnHover
                    pointerOnHover
                    responsive
                    dense
                    striped
                    subHeader
                    subHeaderAlign="left"
                    subHeaderComponent={
                        <div className="row w-100 m-0 p-0">
                            <div className="col-md-2 col-sm-2 col-lg-2 dropdown">
                                <label className="list-label">Select Class</label>
                                <select
                                    style={{ fontSize: "11px" }}
                                    name="status"
                                    className="form-control form-control-sm"
                                    aria-label="Default select example"
                                    value={selectedClass}
                                    onChange={(e) => handleClassChange(e.target.value)}
                                    disabled={isPending}
                                >
                                    <option value="" style={{ fontSize: "11px" }}>Select Class</option>
                                    {classList?.map((item) => (
                                        <option key={item.ClassID} value={item.ClassID}>
                                            {item.ClassName}
                                        </option>
                                    ))}
                                </select>
                                <i className="fas fa-angle-down notificationpositon"></i>
                            </div>

                            <div className="col-md-2 col-sm-2 col-lg-2 dropdown">
                                <label className="list-label">Select Section</label>
                                <select
                                    style={{ fontSize: "11px" }}
                                    name="status"
                                    className="form-control form-control-sm"
                                    aria-label="Default select example"
                                    value={selectedSection}
                                    onChange={(e) => setSelectedSection(e.target.value)}
                                    disabled={!selectedClass || isPending}
                                >
                                    <option value="" style={{ fontSize: "11px" }}>Select Section</option>
                                    {sectionList?.map((item) => (
                                        <option key={item.SectionID} value={item.SectionID}>
                                            {item.SectionName}
                                        </option>
                                    ))}
                                </select>
                                <i className="fas fa-angle-down notificationpositon"></i>
                            </div>

                            <div className="col-12 col-md-5 ms-auto">
                                <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">

                                    {/* Search */}
                                    <input
                                        placeholder="Search..."
                                        ref={searchInput}
                                        type="text"
                                        className="form-control form-control-sm"
                                        style={{ maxWidth: "180px" }}
                                        onChange={searchHandler}
                                        disabled={!deviceCodeList.length}
                                    />

                                    {/* Download */}
                                    <Tippy content="Download Sample Template">
                                        <button
                                            className="exportButtons"
                                            onClick={downloadSampleTemplate}
                                            disabled={!deviceCodeList.length || isPending}
                                        >
                                            <i className="bi bi-file-earmark-excel-fill exportButtonIconsExcel"></i>
                                        </button>
                                    </Tippy>

                                    {/* Import */}
                                    <Tippy content="Import Excel File">
                                        <label className="exportButtons mb-0">
                                            <i className="bi bi-upload exportButtonIconsExcel"></i>
                                            <input
                                                type="file"
                                                accept=".xlsx,.xls"
                                                onChange={handleFileImport}
                                                hidden
                                                disabled={!deviceCodeList.length || isPending || importLoading}
                                            />
                                        </label>
                                    </Tippy>

                                    {/* Update Button */}
                                    <Tippy content="Update Device Codes">
                                        <button
                                            className="exportButtons"
                                            onClick={handleUpdate}
                                            disabled={isPending || !selectedClass || !selectedSection || newDeviceCodeList.length === 0}
                                        >
                                            {isPending ? (
                                                <span className="spinner-border spinner-border-sm"></span>
                                            ) : (
                                                <i className="bi bi-arrow-repeat exportButtonIconsPdf"></i>
                                            )}
                                        </button>
                                    </Tippy>
                                </div>
                            </div>

                        </div>
                    }
                />
            </div>
        </div>
    )
}

export default DeviceCode;